import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { lazy, Suspense } from 'react'
import { ContextProviders } from './contexts/ContextProviders'
import { Layout } from './components/layout/Layout'
import { AdminLayout } from './components/admin/AdminLayout'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { ErrorBoundary } from './components/errors/ErrorBoundary'
import { PageTransition } from './components/transitions/PageTransition'
import { GlobalSearch, useGlobalSearch } from './components/ui/GlobalSearch'
import { ThemeLoadingIndicator } from './components/ui/ThemeLoadingIndicator'
import { PAIChatWidget } from './components/pai/PAIChatWidget'
import { useAuth } from './contexts/AuthContext'
import LandingPage from './pages/LandingPage'
import PortalPage from './pages/PortalPage'
import { SignIn } from '@/components/auth/SignIn'
import { SignUp } from '@/components/auth/SignUp'
import { WebTerminal } from './components/terminal/WebTerminal'

// Lazy load large public pages for better initial load performance
const ProjectsPage = lazy(() => import('./pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })))
const OrganizationsPublicPage = lazy(() => import('./pages/OrganizationsPublicPage').then(m => ({ default: m.OrganizationsPublicPage })))
const RolesPublicPage = lazy(() => import('./pages/RolesPublicPage').then(m => ({ default: m.RolesPublicPage })))
const BackgroundPage = lazy(() => import('./pages/BackgroundPage').then(m => ({ default: m.BackgroundPage })))
const InterestsPage = lazy(() => import('./pages/InterestsPage').then(m => ({ default: m.InterestsPage })))
const WritingsPage = lazy(() => import('./pages/WritingsPage').then(m => ({ default: m.WritingsPage })))
const PortfolioIntelligencePage = lazy(() => import('./pages/PortfolioIntelligencePage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })))
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })))
const HeroDemo = lazy(() => import('./pages/HeroDemo'))

// Lazy load admin pages to avoid including Prisma in main bundle
const DashboardPage = lazy(() => import('./pages/admin/DashboardPage').then(m => ({ default: m.DashboardPage })))
const ThemeControlsPage = lazy(() => import('./pages/admin/ThemeControlsPage'))
const AdminProjectsPage = lazy(() => import('./pages/admin/ProjectsPage').then(m => ({ default: m.ProjectsPage })))
const OrganizationsPage = lazy(() => import('./pages/admin/OrganizationsPage').then(m => ({ default: m.OrganizationsPage })))
const RolesPage = lazy(() => import('./pages/admin/RolesPage').then(m => ({ default: m.RolesPage })))
const EvaluationsPage = lazy(() => import('./pages/admin/EvaluationsPage').then(m => ({ default: m.EvaluationsPage })))
const ContentAdminPage = lazy(() => import('./pages/ContentAdminPage').then(m => ({ default: m.ContentAdminPage })))
const ContentDashboard = lazy(() => import('./pages/admin/ContentDashboard').then(m => ({ default: m.ContentDashboard })))
const AssetManagementPage = lazy(() => import('./pages/admin/AssetManagementPage').then(m => ({ default: m.AssetManagementPage })))
const BioBuilderPage = lazy(() => import('./pages/admin/BioBuilderPage').then(m => ({ default: m.BioBuilderPage })))

// Onboarding pages
const OnboardingPage = lazy(() => import('./pages/OnboardingPage').then(m => ({ default: m.OnboardingPage })))
const OnboardingResumePage = lazy(() => import('./pages/OnboardingResumePage').then(m => ({ default: m.OnboardingResumePage })))

// Initialize React Query client with stale-while-revalidate strategy
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale-while-revalidate configuration
      staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes - unused data is garbage collected after 10 minutes

      // Automatic background refetching
      refetchOnWindowFocus: true, // Refetch when user returns to tab (shows stale data immediately)
      refetchOnReconnect: true, // Refetch when internet connection is restored
      refetchOnMount: 'always', // Always refetch on component mount (shows cached data first)

      // Error handling
      retry: 1, // Retry failed requests once
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff

      // Performance
      networkMode: 'online', // Only run queries when online
    },
    mutations: {
      // Mutation defaults
      retry: 0, // Don't retry mutations by default
      networkMode: 'online',
    },
  },
})

// Inner component that uses auth context (must be inside ContextProviders)
function AppContent() {
  const { isOpen, closeSearch } = useGlobalSearch()
  const { isAdmin } = useAuth()

  return (
    <BrowserRouter>
      {/* PI7: Theme transition loading indicator */}
      <ThemeLoadingIndicator />

      {/* Global Search Modal (Cmd+K / Ctrl+K) */}
      <GlobalSearch isOpen={isOpen} onClose={closeSearch} />

      {/* PAI Chat Widget - ADMIN ONLY
          Shows only when logged in as admin (works in production)
          Persona ID: 295d7e0b-d6e1-4077-8379-6b86c20470c3
          TELOS documents initialized: ✅ (10 documents)
      */}
      {isAdmin && (
        <PAIChatWidget
          personaId="295d7e0b-d6e1-4077-8379-6b86c20470c3"
          position="bottom-right"
        />
      )}

      <Routes>
            {/* Portal page - theme selector entry point */}
            <Route path="/" element={<PageTransition><PortalPage /></PageTransition>} />

            {/* Main public pages with standard layout */}
            <Route path="/explore" element={<PageTransition><Layout><LandingPage /></Layout></PageTransition>} />
            <Route path="/projects" element={<PageTransition><Layout><ProjectsPage /></Layout></PageTransition>} />
            <Route path="/organizations" element={<PageTransition><Layout><OrganizationsPublicPage /></Layout></PageTransition>} />
            <Route path="/roles" element={<PageTransition><Layout><RolesPublicPage /></Layout></PageTransition>} />
            <Route path="/background" element={<PageTransition><Layout><BackgroundPage /></Layout></PageTransition>} />
            <Route path="/interests" element={<PageTransition><Layout><InterestsPage /></Layout></PageTransition>} />
            <Route path="/writings" element={<PageTransition><Layout><WritingsPage /></Layout></PageTransition>} />
            <Route path="/portfolio-intelligence" element={<PageTransition><Layout><PortfolioIntelligencePage /></Layout></PageTransition>} />
            <Route path="/cv" element={<PageTransition><Layout><div>CV (coming soon)</div></Layout></PageTransition>} />
            <Route path="/privacy" element={<PageTransition><Layout><PrivacyPage /></Layout></PageTransition>} />
            <Route path="/terms" element={<PageTransition><Layout><TermsPage /></Layout></PageTransition>} />

            {/* Hero Demo (development) */}
            <Route path="/hero-demo" element={<PageTransition><HeroDemo /></PageTransition>} />

            {/* Admin pages with admin layout */}
            <Route
              path="/admin"
              element={
                <PageTransition>
                  <ProtectedRoute requiredRole="admin">
                    <Layout showNavigation={false}>
                      <AdminLayout>
                        <Suspense fallback={<div>Loading...</div>}>
                          <DashboardPage />
                        </Suspense>
                      </AdminLayout>
                    </Layout>
                  </ProtectedRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/theme-controls"
              element={
                <PageTransition>
                  <ProtectedRoute requiredRole="admin">
                    <Layout showNavigation={false}>
                      <Suspense fallback={<div>Loading...</div>}>
                        <ThemeControlsPage />
                      </Suspense>
                    </Layout>
                  </ProtectedRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/projects"
              element={
                <PageTransition>
                  <ProtectedRoute requiredRole="admin">
                    <Layout showNavigation={false}>
                      <AdminLayout>
                        <Suspense fallback={<div>Loading...</div>}>
                          <AdminProjectsPage />
                        </Suspense>
                      </AdminLayout>
                    </Layout>
                  </ProtectedRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/organizations"
              element={
                <PageTransition>
                  <ProtectedRoute requiredRole="admin">
                    <Layout showNavigation={false}>
                      <AdminLayout>
                        <Suspense fallback={<div>Loading...</div>}>
                          <OrganizationsPage />
                        </Suspense>
                      </AdminLayout>
                    </Layout>
                  </ProtectedRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/roles"
              element={
                <PageTransition>
                  <ProtectedRoute requiredRole="admin">
                    <Layout showNavigation={false}>
                      <AdminLayout>
                        <Suspense fallback={<div>Loading...</div>}>
                          <RolesPage />
                        </Suspense>
                      </AdminLayout>
                    </Layout>
                  </ProtectedRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/evaluations"
              element={
                <PageTransition>
                  <ProtectedRoute requiredRole="admin">
                    <Layout showNavigation={false}>
                      <AdminLayout>
                        <Suspense fallback={<div>Loading...</div>}>
                          <EvaluationsPage />
                        </Suspense>
                      </AdminLayout>
                    </Layout>
                  </ProtectedRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/content"
              element={
                <PageTransition>
                  <ProtectedRoute requiredRole="admin">
                    <Layout showNavigation={false}>
                      <AdminLayout>
                        <Suspense fallback={<div>Loading...</div>}>
                          <ContentDashboard />
                        </Suspense>
                      </AdminLayout>
                    </Layout>
                  </ProtectedRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/content-db"
              element={
                <PageTransition>
                  <ProtectedRoute requiredRole="admin">
                    <Layout showNavigation={false}>
                      <AdminLayout>
                        <Suspense fallback={<div>Loading...</div>}>
                          <ContentAdminPage />
                        </Suspense>
                      </AdminLayout>
                    </Layout>
                  </ProtectedRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/cv"
              element={
                <PageTransition>
                  <ProtectedRoute requiredRole="admin">
                    <Layout showNavigation={false}>
                      <AdminLayout>
                        <div>Secure CV Management (coming soon)</div>
                      </AdminLayout>
                    </Layout>
                  </ProtectedRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/assets"
              element={
                <PageTransition>
                  <ProtectedRoute requiredRole="admin">
                    <Layout showNavigation={false}>
                      <AdminLayout>
                        <Suspense fallback={<div>Loading...</div>}>
                          <AssetManagementPage />
                        </Suspense>
                      </AdminLayout>
                    </Layout>
                  </ProtectedRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/bio-builder"
              element={
                <PageTransition>
                  <ProtectedRoute requiredRole="admin">
                    <Layout showNavigation={false}>
                      <AdminLayout>
                        <Suspense fallback={<div>Loading...</div>}>
                          <BioBuilderPage />
                        </Suspense>
                      </AdminLayout>
                    </Layout>
                  </ProtectedRoute>
                </PageTransition>
              }
            />

            {/* Onboarding routes */}
            <Route
              path="/onboarding"
              element={
                <PageTransition>
                  <Suspense fallback={<div>Loading...</div>}>
                    <OnboardingPage />
                  </Suspense>
                </PageTransition>
              }
            />
            <Route
              path="/onboarding/resume"
              element={
                <PageTransition>
                  <Suspense fallback={<div>Loading...</div>}>
                    <OnboardingResumePage />
                  </Suspense>
                </PageTransition>
              }
            />

            {/* Terminal Route - Superadmin only */}
            <Route
              path="/terminal"
              element={
                <PageTransition>
                  <ProtectedRoute requiredRole="admin">
                    <ErrorBoundary>
                      <WebTerminal />
                    </ErrorBoundary>
                  </ProtectedRoute>
                </PageTransition>
              }
            />

            {/* Stack Auth handler routes */}
            <Route path="/handler/sign-in" element={<PageTransition><SignIn fullPage /></PageTransition>} />
            <Route path="/handler/sign-up" element={<PageTransition><SignUp fullPage /></PageTransition>} />
            <Route path="/handler/callback" element={<PageTransition><div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Authenticating...</div></PageTransition>} />

            {/* 404 fallback */}
            <Route path="*" element={<PageTransition><Layout><div>Page not found</div></Layout></PageTransition>} />
            </Routes>
    </BrowserRouter>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ContextProviders>
          <AppContent />
        </ContextProviders>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
